import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { ReactNode } from 'react'

interface PurchasingDialogProps {
    isOpen: boolean
    onClose?: () => void
    /** Linha pequena acima do título (ex.: "Cotação · Fornecedor X"). */
    eyebrow?: string
    title: string
    description?: ReactNode
    size?: 'md' | 'lg'
    footer?: ReactNode
    children: ReactNode
    testId?: string
}

/**
 * Casca padrão dos modais de Compras: cabeçalho fixo, corpo com rolagem
 * própria e rodapé fixo com as ações — o conteúdo sempre abre do topo.
 */
export const PurchasingDialog = ({
    isOpen,
    onClose,
    eyebrow,
    title,
    description,
    size = 'md',
    footer,
    children,
    testId,
}: PurchasingDialogProps) => (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.() }}>
        <DialogContent
            data-testid={testId}
            className={cn(
                'flex max-h-[92vh] w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-card p-0',
                size === 'lg' ? 'max-w-2xl' : 'max-w-lg'
            )}
        >
            <header className="border-b border-border px-6 pb-4 pt-5 pr-12">
                {eyebrow && (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-pink">{eyebrow}</p>
                )}
                <DialogTitle className="mt-0.5 font-display text-xl font-bold leading-tight text-foreground">{title}</DialogTitle>
                {description && (
                    <DialogDescription className="mt-1 text-sm text-muted-foreground">{description}</DialogDescription>
                )}
            </header>
            <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
            {footer && (
                <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-secondary/50 px-6 py-3">
                    {footer}
                </footer>
            )}
        </DialogContent>
    </Dialog>
)

export const FormSection = ({ title, hint, children, className }: { title: string; hint?: ReactNode; children: ReactNode; className?: string }) => (
    <section className={cn('border-b border-dashed border-border pb-5 last:border-b-0 last:pb-0 [&+&]:pt-5', className)}>
        <h3 className="text-xs font-bold uppercase tracking-[0.08em] text-brand-deep">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        <div className="mt-3">{children}</div>
    </section>
)

export const Field = ({ label, htmlFor, error, hint, children, className }: {
    label: string
    htmlFor?: string
    error?: string
    hint?: ReactNode
    children: ReactNode
    className?: string
}) => (
    <div className={className}>
        <label htmlFor={htmlFor} className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            {label}
        </label>
        {children}
        {error ? (
            <span className="mt-1 block text-xs text-od-secondary-600" role="alert">{error}</span>
        ) : hint ? (
            <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>
        ) : null}
    </div>
)
